import { SearchIcon } from './icons';

/**
 * SearchInput — VERBATIM port from ~/FrontendFinal/Design/src/components/SearchInput.tsx.
 * Only the icon import path is changed (`./icons`); every class, token and behaviour is the
 * Design's. See the Design file's header for the full SVG provenance.
 *
 * Built from `Search Bar.svg`, which draws 2 sizes x 4 shapes plus 4 icon-only
 * variants — 12 boxes in total.
 *
 * THE FILL IS --accent-subtle, NOT --accent. Every one of the twelve is
 * `fill="#009D9A" fill-opacity="0.1"`, and P1's instance at
 * `x=222 y=322 w=792 h=23 rx=8` carries the same 0.1.
 *
 * THE OUTLINED VARIANT IS NOT TRANSPARENT either. Each of the four outlined
 * boxes is drawn as TWO stacked rects at identical geometry: a
 * `fill=#009D9A fill-opacity=0.1` beneath a `stroke=#009D9A` with no fill. So the
 * outlined variant is the same subtle fill plus a stroke, not a stroke alone.
 *
 * NOT A REAL INPUT BY DEFAULT, deliberately. The design draws a static "Search"
 * label. A real `<input>` with a `placeholder` is a functional decision, so it
 * stays OPT-IN: pass `value` + `onChange` and the label span is swapped for a real
 * `<input type="search">`. The picker (P1) passes them.
 */

export type SearchSize = 'sm' | 'xs';
export type SearchShape = 'square' | 'pill';

/**
 * Height, text size and radius per size x shape, read off the sheet.
 * `xs` pill is 11.5 in the source, which is 23/2 — a true semicircle. `sm` pill
 * is 16, which is 32/2.
 */
const SIZE: Record<
  SearchSize,
  { height: string; text: string; radius: Record<SearchShape, string> }
> = {
  sm: {
    height: 'h-pill',
    text: 'text-sm',
    radius: { square: 'rounded-md', pill: 'rounded-lg' },
  },
  xs: {
    height: 'h-[23px]',
    text: 'text-xs',
    radius: { square: 'rounded-md', pill: 'rounded-[11.5px]' },
  },
};

export interface SearchInputProps {
  size?: SearchSize;
  shape?: SearchShape;
  /** Outlined variant: 1px --accent stroke OVER the same --accent-subtle fill. */
  outlined?: boolean;
  /** Omit to render the icon-only square variant. */
  label?: string;
  /** Fills its container. The sheet draws fixed widths; P1 draws it full-bleed. */
  block?: boolean;
  /**
   * Pass BOTH to make it a real controlled `<input type="search">` (the label
   * becomes the placeholder). Omit both to keep the static label the design draws.
   */
  value?: string;
  onChange?: (value: string) => void;
  className?: string;
}

export function SearchInput({
  size = 'sm',
  shape = 'square',
  outlined = false,
  label = 'Search',
  block = false,
  value,
  onChange,
  className = '',
}: SearchInputProps) {
  const s = SIZE[size];
  const iconOnly = label === '';
  // Real input only when wired; otherwise the drawn static label. iconOnly has
  // no label to type into, so it never becomes an input.
  const editable = !iconOnly && onChange != null;

  return (
    <div
      className={[
        'inline-flex items-center gap-5 px-pill-x',
        s.height,
        s.radius[shape],
        // The sheet's icon-only variants are squares (32 and 24), so they get
        // no horizontal padding beyond centring.
        iconOnly ? 'justify-center px-0 aspect-square' : '',
        block ? 'flex w-full' : '',
        // --accent-subtle in BOTH variants; the outline only adds the stroke.
        'bg-accent-subtle',
        outlined ? 'border border-accent' : '',
        s.text,
        'font-light text-text',
        className,
      ].join(' ')}
    >
      <SearchIcon className="shrink-0 text-icon" />
      {editable ? (
        // Transparent, borderless input filling the box — it inherits the box's
        // font size/weight/colour so it reads exactly like the static label,
        // and the drawn box geometry is untouched. The label is the placeholder.
        <input
          type="search"
          value={value ?? ''}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={label}
          aria-label={label}
          className="w-full min-w-0 bg-transparent text-inherit placeholder:text-text focus:outline-none"
        />
      ) : (
        !iconOnly && <span className="truncate">{label}</span>
      )}
    </div>
  );
}
